'use client'

import { type Reducer } from 'react'

import { defaultState, getDefaultSort } from './defaultState'
import type { SearchAction } from './stateActions'
import { LISTINGS_SORT_RELEVANCE } from './types'
import type { ListingFiltersState, ListingSearchState } from './types'

// `id` is populated exclusively by useMyBidsListingIdSync while myBids is on,
// or by useWatchlistBidStatusSync while bidStatus[] is non-empty. Whenever a
// filters transition turns either off, clear it in the same dispatch —
// otherwise the state->URL effect can observe a stale `id` + off filter
// combination for a render before the separate cleanup effect runs, and leak
// it into a `router.push` that corrupts browser history (see BP-2322).
const clearIdIfDerivedFilterTurnedOff = (
  prevFilters: ListingFiltersState,
  nextFilters: ListingFiltersState,
  id: ListingSearchState['id']
): ListingSearchState['id'] => {
  const myBidsTurnedOff = prevFilters.myBids && !nextFilters.myBids
  const bidStatusTurnedOff = !!prevFilters.bidStatus && !nextFilters.bidStatus
  return myBidsTurnedOff || bidStatusTurnedOff ? null : id
}

const listingSearchReducer =
  (): Reducer<ListingSearchState, SearchAction> => (state, action) => {
    switch (action.type) {
      case 'SET_STATE':
        return {
          ...state,
          ...action.partialState,
          appliedFromSavedSearch: action.appliedFromSavedSearch || false,
          applyingSearchQuery: action.applyingSearchQuery || false,
        }
      case 'RESET_STATE':
        return {
          ...state,
          appliedFromSavedSearch: false,
          applyingSearchQuery: false,
        }
      case 'SET_SORT':
        return {
          ...state,
          page: 1,
          sortBy: action.sortBy,
          sortOrder: action.sortOrder,
          appliedFromSavedSearch: false,
          applyingSearchQuery: false,
        }
      case 'SET_PAGE_SIZE':
        return {
          ...state,
          page: 1,
          pageSize: action.pageSize,
          // Explicitly preserve id to ensure it's not lost during state updates
          id: state.id,
        }
      case 'SET_VIEW':
        return {
          ...state,
          view: action.view,
        }
      case 'SET_SORT_FROZEN':
        return {
          ...state,
          isSortFrozen: action.isSortFrozen,
        }
      case 'SET_PAGE':
        return {
          ...state,
          page: action.page,
          appliedFromSavedSearch: false,
          applyingSearchQuery: false,
        }
      case 'SET_FILTERS': {
        const [relevanceSortBy, relevanceSortOrder] = LISTINGS_SORT_RELEVANCE
        const nextFilters = {
          ...defaultState.filters,
          ...action.filters,
        }
        return {
          ...state,
          filters: nextFilters,
          id: clearIdIfDerivedFilterTurnedOff(
            state.filters,
            nextFilters,
            state.id
          ),
          page: 1,
          // Saved searches don't persist sort; always use relevance when applying a query
          ...(action.applyingSearchQuery && {
            sortBy: relevanceSortBy,
            sortOrder: relevanceSortOrder,
          }),
          appliedFromSavedSearch: action.appliedFromSavedSearch || false,
          applyingSearchQuery: action.applyingSearchQuery || false,
        }
      }
      case 'UPDATE_FILTERS': {
        const updatedFilters = {
          ...state.filters,
          ...action.filters,
        }
        // If clearing search query and sort is relevance, reset to the current view's default
        const shouldResetSort =
          action.filters.q === null && state.sortBy === 'relevance'
        const [resetSortBy, resetSortOrder] = getDefaultSort({
          hasQuery: !!updatedFilters.q,
          view: state.view,
        })

        return {
          ...state,
          filters: updatedFilters,
          id: clearIdIfDerivedFilterTurnedOff(
            state.filters,
            updatedFilters,
            state.id
          ),
          page: 1,
          ...(shouldResetSort && {
            sortBy: resetSortBy,
            sortOrder: resetSortOrder,
          }),
          appliedFromSavedSearch: false,
          applyingSearchQuery: false,
        }
      }
      case 'RESET_FILTERS':
        return {
          ...state,
          filters: defaultState.filters,
          id: clearIdIfDerivedFilterTurnedOff(
            state.filters,
            defaultState.filters,
            state.id
          ),
          page: 1,
          appliedFromSavedSearch: false,
          applyingSearchQuery: false,
        }
      case 'ADD_ID': {
        const currentIds = state.id ?? []
        if (currentIds.includes(action.id)) return state
        return { ...state, id: [...currentIds, action.id] }
      }
      case 'REMOVE_ID': {
        if (!state.id) return state
        return {
          ...state,
          id: state.id.filter((id) => id !== action.id),
          page: 1,
        }
      }
      case 'RESET_SORT': {
        const [defaultSortBy, defaultSortOrder] = getDefaultSort({
          hasQuery: !!state.filters.q,
          view: state.view,
        })
        return {
          ...state,
          page: 1,
          sortBy: defaultSortBy,
          sortOrder: defaultSortOrder,
          appliedFromSavedSearch: false,
          applyingSearchQuery: false,
        }
      }
    }
  }

export default listingSearchReducer
